"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MERGE_COLLECT_OPTIONS = exports.MERGE_STRATEGY_OPTIONS = exports.DEFAULT_LOOP_CONFIG = exports.LOOP_MODE_OPTIONS = exports.BRANCH_OPERATORS = exports.BRANCH_TYPE_OPTIONS = exports.DEFAULT_BRANCH_PORTS = exports.BRANCH_OTHERWISE_OPERATOR = exports.DEFAULT_BRANCH_TYPE = exports.CONTROL_BRANCH_TYPES = void 0;
exports.branchDefaultPortId = branchDefaultPortId;
exports.branchSlugForType = branchSlugForType;
exports.defaultMergeStrategyForBranch = defaultMergeStrategyForBranch;
exports.buildBranchInputSlots = buildBranchInputSlots;
exports.buildBranchConfigSlots = buildBranchConfigSlots;
exports.buildBranchOutputSlots = buildBranchOutputSlots;
exports.buildLoopInputSlots = buildLoopInputSlots;
exports.projectLoopConfig = projectLoopConfig;
exports.buildLoopConfigSlots = buildLoopConfigSlots;
exports.buildLoopOutputSlots = buildLoopOutputSlots;
exports.buildMergeInputSlots = buildMergeInputSlots;
exports.buildMergeConfigSlots = buildMergeConfigSlots;
exports.buildMergeOutputSlots = buildMergeOutputSlots;
exports.buildSplitOutputSlots = buildSplitOutputSlots;
exports.buildFilterOutputSlots = buildFilterOutputSlots;
exports.buildJoinOutputSlots = buildJoinOutputSlots;
exports.deriveControlNodeOutputSlots = deriveControlNodeOutputSlots;
exports.CONTROL_BRANCH_TYPES = new Set([
    'branch_xor',
    'branch_or',
    'branch_and',
]);
exports.DEFAULT_BRANCH_TYPE = 'branch_xor';
/**
 * The route operator of the else route: it takes the run when no other route
 * matches. A branch without one routes nowhere then (the paired merge sees
 * no route), unless an API-built graph names a `defaultPortId`.
 */
exports.BRANCH_OTHERWISE_OPERATOR = 'otherwise';
/** The route a branch takes when no conditional route matches. */
function branchDefaultPortId(config) {
    return (config.ports?.find(port => port.condition?.operator === exports.BRANCH_OTHERWISE_OPERATOR)?.portId ?? config.defaultPortId);
}
exports.DEFAULT_BRANCH_PORTS = [
    {
        portId: 'true',
        label: 'Route 1',
        condition: { expression: '', operator: 'eq', value: true },
    },
    {
        portId: 'false',
        label: 'Route 2',
        condition: { expression: '', operator: 'eq', value: false },
    },
];
exports.BRANCH_TYPE_OPTIONS = [
    {
        value: 'branch_xor',
        label: 'XOR - first match',
        labelKey: 'com.streamlain.streamlain.workflow.dock.branch-mode-xor',
    },
];
exports.BRANCH_OPERATORS = [
    { value: 'exists', label: 'exists' },
    { value: 'eq', label: 'equals' },
    { value: 'neq', label: 'not equals' },
    { value: 'gt', label: '>' },
    { value: 'lt', label: '<' },
    { value: 'gte', label: '>=' },
    { value: 'lte', label: '<=' },
    { value: 'contains', label: 'contains' },
    { value: 'isEmpty', label: 'is empty' },
    { value: exports.BRANCH_OTHERWISE_OPERATOR, label: 'otherwise' },
];
exports.LOOP_MODE_OPTIONS = [
    {
        value: 'counter',
        label: 'Counter - start -> end',
        labelKey: 'com.streamlain.streamlain.workflow.dock.loop-mode-counter',
    },
    {
        value: 'condition',
        label: 'Condition - while truthy',
        labelKey: 'com.streamlain.streamlain.workflow.dock.loop-mode-condition',
    },
    {
        value: 'array',
        label: 'Array - for each item',
        labelKey: 'com.streamlain.streamlain.workflow.dock.loop-mode-array',
    },
];
exports.DEFAULT_LOOP_CONFIG = {
    mode: 'array',
    maxIterations: 100,
    counterVariable: 'i',
    start: 0,
    end: 10,
    increment: 1,
    conditionExpression: '',
    sourceArray: '',
    itemVariable: 'item',
};
const OUTPUT_SECTION = 'Output';
const CONSTANTS_SECTION = 'Constants';
exports.MERGE_STRATEGY_OPTIONS = [
    { value: 'wait_all', label: 'Wait for all' },
    { value: 'wait_any', label: 'Wait for any' },
    { value: 'wait_first', label: 'First to arrive' },
];
exports.MERGE_COLLECT_OPTIONS = [
    { value: 'object', label: 'Object - keyed by upstream port' },
    { value: 'array', label: 'Array - ordered' },
];
function branchSlugForType(type) {
    return `control-${type}`;
}
function defaultMergeStrategyForBranch(type) {
    return type === 'branch_xor' ? 'wait_first' : 'wait_all';
}
function slotId(slug, name) {
    return `slot-${slug}-${name}`;
}
function withValue(slot, value) {
    return { ...slot, value };
}
function clonePorts(ports = exports.DEFAULT_BRANCH_PORTS) {
    return ports.map(port => ({
        portId: port.portId,
        label: port.label,
        condition: { ...port.condition },
    }));
}
function buildBranchInputSlots(_slug) {
    return [];
}
function buildBranchConfigSlots({ slug, condition = '', ports = exports.DEFAULT_BRANCH_PORTS, pairedMergeId, }) {
    return [
        withValue({
            id: slotId(slug, 'condition'),
            name: 'condition',
            labelsI18n: { en: 'Value to Check', de: 'Prüfwert' },
            type: 'text',
            required: true,
            bindable: true,
            defaultValue: '',
            description: 'Value evaluated against each route. Type a constant or bind an upstream output.',
            descriptionsI18n: {
                en: 'Value evaluated against each route. Type a constant or bind an upstream output.',
                de: 'Wert, der gegen jede Route geprüft wird. Direkt eintragen oder aus einem vorherigen Step binden.',
            },
            section: CONSTANTS_SECTION,
        }, condition),
        withValue({
            id: slotId(slug, 'ports'),
            name: 'ports',
            labelsI18n: { en: 'Routes', de: 'Routen' },
            type: 'json',
            required: true,
            bindable: false,
            description: 'One outgoing port per conditional route. Each route compares the branch value with an operator and expected value.',
            descriptionsI18n: {
                en: 'One outgoing port per conditional route. Each route compares the branch value with an operator and expected value.',
                de: 'Ein ausgehender Port pro bedingter Route. Jede Route vergleicht den Prüfwert mit Operator und Erwartungswert.',
            },
            widget: 'port-editor',
            widgetProps: {
                operators: exports.BRANCH_OPERATORS,
                sourceSlotName: 'condition',
            },
            defaultValue: clonePorts(),
            section: CONSTANTS_SECTION,
        }, clonePorts(ports)),
        withValue({
            id: slotId(slug, 'pairedMergeId'),
            name: 'pairedMergeId',
            labelsI18n: { en: 'Paired Merge', de: 'Zugehörige Zusammenführung' },
            type: 'text',
            required: false,
            bindable: false,
            description: 'Step id of the auto-created Merge node. Set by the UI at insert time - not user-editable.',
            descriptionsI18n: {
                en: 'Step id of the auto-created Merge node. Set by the UI at insert time - not user-editable.',
                de: 'Step-ID der automatisch erstellten Merge-Node. Wird beim Einfügen durch die UI gesetzt und ist nicht editierbar.',
            },
            widget: 'hidden',
            section: CONSTANTS_SECTION,
        }, pairedMergeId),
    ];
}
function buildBranchOutputSlots() {
    return [];
}
function buildLoopInputSlots() {
    return [];
}
function projectLoopConfig(config) {
    return {
        ...exports.DEFAULT_LOOP_CONFIG,
        ...config,
    };
}
function buildLoopConfigSlots(config) {
    const cfg = projectLoopConfig(config);
    const loopSlug = 'control-loop';
    return [
        withValue({
            id: slotId(loopSlug, 'mode'),
            name: 'mode',
            labelsI18n: { en: 'Mode', de: 'Modus' },
            type: 'enum',
            required: true,
            bindable: false,
            options: exports.LOOP_MODE_OPTIONS,
            defaultValue: exports.DEFAULT_LOOP_CONFIG.mode,
            widget: 'enum-select',
            section: CONSTANTS_SECTION,
            description: 'Iteration strategy: counter (numeric range), condition (while truthy), or array (for-each).',
            descriptionsI18n: {
                en: 'Iteration strategy: counter (numeric range), condition (while truthy), or array (for-each).',
                de: 'Iterationsstrategie: Counter (numerischer Bereich), Bedingung (solange truthy) oder Array (for-each).',
            },
        }, cfg.mode),
        withValue({
            id: slotId(loopSlug, 'maxIterations'),
            name: 'maxIterations',
            labelsI18n: { en: 'Max Iterations', de: 'Max. Iterationen' },
            type: 'number',
            required: true,
            bindable: false,
            defaultValue: exports.DEFAULT_LOOP_CONFIG.maxIterations,
            widget: 'input-number',
            widgetProps: { min: 1, max: 10000 },
            section: CONSTANTS_SECTION,
            description: 'Hard ceiling on iterations regardless of mode - guards against runaway loops.',
            descriptionsI18n: {
                en: 'Hard ceiling on iterations regardless of mode - guards against runaway loops.',
                de: 'Harte Obergrenze für Iterationen, unabhängig vom Modus. Schützt vor Endlosschleifen.',
            },
        }, cfg.maxIterations),
        withValue({
            id: slotId(loopSlug, 'counterVariable'),
            name: 'counterVariable',
            labelsI18n: { en: 'Counter Variable', de: 'Counter-Variable' },
            type: 'text',
            required: false,
            bindable: false,
            defaultValue: exports.DEFAULT_LOOP_CONFIG.counterVariable,
            widget: 'input-text',
            section: CONSTANTS_SECTION,
            visibleIf: { field: 'mode', equals: 'counter' },
            description: 'Variable name exposed to downstream steps carrying the current iteration count.',
            descriptionsI18n: {
                en: 'Variable name exposed to downstream steps carrying the current iteration count.',
                de: 'Variablenname für Folge-Steps, der den aktuellen Iterationszähler trägt.',
            },
        }, cfg.counterVariable),
        withValue({
            id: slotId(loopSlug, 'start'),
            name: 'start',
            labelsI18n: { en: 'Start', de: 'Start' },
            type: 'number',
            required: false,
            bindable: false,
            defaultValue: exports.DEFAULT_LOOP_CONFIG.start,
            widget: 'input-number',
            section: CONSTANTS_SECTION,
            visibleIf: { field: 'mode', equals: 'counter' },
            description: 'Initial counter value (counter mode).',
            descriptionsI18n: {
                en: 'Initial counter value (counter mode).',
                de: 'Initialer Counter-Wert (Counter-Modus).',
            },
        }, cfg.start),
        withValue({
            id: slotId(loopSlug, 'end'),
            name: 'end',
            labelsI18n: { en: 'End', de: 'Ende' },
            type: 'number',
            required: false,
            bindable: false,
            defaultValue: exports.DEFAULT_LOOP_CONFIG.end,
            widget: 'input-number',
            section: CONSTANTS_SECTION,
            visibleIf: { field: 'mode', equals: 'counter' },
            description: 'Counter value at which the loop terminates (exclusive).',
            descriptionsI18n: {
                en: 'Counter value at which the loop terminates (exclusive).',
                de: 'Counter-Wert, bei dem die Schleife endet (exklusiv).',
            },
        }, cfg.end),
        withValue({
            id: slotId(loopSlug, 'increment'),
            name: 'increment',
            labelsI18n: { en: 'Increment', de: 'Schrittweite' },
            type: 'number',
            required: false,
            bindable: false,
            defaultValue: exports.DEFAULT_LOOP_CONFIG.increment,
            widget: 'input-number',
            section: CONSTANTS_SECTION,
            visibleIf: { field: 'mode', equals: 'counter' },
            description: 'Step added to the counter each iteration (counter mode).',
            descriptionsI18n: {
                en: 'Step added to the counter each iteration (counter mode).',
                de: 'Wert, der pro Iteration zum Counter addiert wird (Counter-Modus).',
            },
        }, cfg.increment),
        withValue({
            id: slotId(loopSlug, 'conditionExpression'),
            name: 'conditionExpression',
            labelsI18n: { en: 'Condition', de: 'Bedingung' },
            type: 'text',
            required: false,
            bindable: false,
            widget: 'template-input',
            widgetProps: { placeholder: '{{step.counter}} < 10' },
            section: CONSTANTS_SECTION,
            visibleIf: { field: 'mode', equals: 'condition' },
            description: 'Template expression evaluated each iteration - loop continues while truthy.',
            descriptionsI18n: {
                en: 'Template expression evaluated each iteration - loop continues while truthy.',
                de: 'Template-Ausdruck, der pro Iteration ausgewertet wird. Die Schleife läuft weiter, solange der Wert truthy ist.',
            },
        }, cfg.conditionExpression),
        withValue({
            id: slotId(loopSlug, 'sourceArray'),
            name: 'sourceArray',
            labelsI18n: { en: 'Source List', de: 'Quell-Liste' },
            type: 'text',
            required: false,
            bindable: true,
            widgetProps: {
                allowLiteralInput: true,
                multilineLiteralInput: true,
                literalRows: 4,
                placeholder: 'alpha\nbeta\ngamma',
            },
            section: CONSTANTS_SECTION,
            visibleIf: { field: 'mode', equals: 'array' },
            description: 'List to iterate over. Bind an upstream list, type or paste one item per line, or provide a single value. JSON arrays also work, but are optional.',
            descriptionsI18n: {
                en: 'List to iterate over. Bind an upstream list, type or paste one item per line, or provide a single value. JSON arrays also work, but are optional.',
                de: 'Liste, über die iteriert wird. Aus einem vorherigen Listen-Output binden, ein Item pro Zeile einfügen oder einen einzelnen Wert nutzen. JSON-Arrays funktionieren auch, sind aber optional.',
            },
        }, cfg.sourceArray),
        withValue({
            id: slotId(loopSlug, 'itemVariable'),
            name: 'itemVariable',
            labelsI18n: { en: 'Item Variable', de: 'Item-Variable' },
            type: 'text',
            required: false,
            bindable: false,
            defaultValue: exports.DEFAULT_LOOP_CONFIG.itemVariable,
            widget: 'input-text',
            section: CONSTANTS_SECTION,
            visibleIf: { field: 'mode', equals: 'array' },
            description: 'Variable name exposed to downstream steps holding the current array element (array mode).',
            descriptionsI18n: {
                en: 'Variable name exposed to downstream steps holding the current array element (array mode).',
                de: 'Variablenname für Folge-Steps, der das aktuelle Array-Element enthält (Array-Modus).',
            },
        }, cfg.itemVariable),
    ];
}
function buildLoopOutputSlots() {
    const loopSlug = 'control-loop';
    return [
        {
            id: slotId(loopSlug, 'item'),
            name: 'item',
            labelsI18n: { en: 'Item', de: 'Item' },
            type: 'json',
            required: false,
            description: 'Current iteration value. counter: number; condition: last evaluated payload; list: current item.',
            descriptionsI18n: {
                en: 'Current iteration value. counter: number; condition: last evaluated payload; list: current item.',
                de: 'Aktueller Iterationswert. Counter: Zahl; Bedingung: zuletzt ausgewerteter Payload; Liste: aktuelles Item.',
            },
            section: OUTPUT_SECTION,
        },
        {
            id: slotId(loopSlug, 'index'),
            name: 'index',
            labelsI18n: { en: 'Index', de: 'Index' },
            type: 'number',
            required: false,
            description: 'Zero-based iteration counter.',
            descriptionsI18n: {
                en: 'Zero-based iteration counter.',
                de: 'Nullbasierter Iterationszähler.',
            },
            section: OUTPUT_SECTION,
        },
        {
            id: slotId(loopSlug, 'result'),
            name: 'result',
            labelsI18n: { en: 'Result', de: 'Ergebnis' },
            type: 'json',
            required: false,
            description: 'List of per-iteration body outputs.',
            descriptionsI18n: {
                en: 'List of per-iteration body outputs.',
                de: 'Liste der Body-Ausgaben pro Iteration.',
            },
            section: OUTPUT_SECTION,
        },
    ];
}
function buildMergeInputSlots() {
    return [];
}
function buildMergeConfigSlots(config) {
    const mergeSlug = 'control-merge';
    const strategy = config?.strategy ?? 'wait_all';
    const collectMode = config?.collectMode ?? 'object';
    return [
        withValue({
            id: slotId(mergeSlug, 'strategy'),
            name: 'strategy',
            labelsI18n: { en: 'Strategy', de: 'Strategie' },
            type: 'enum',
            required: true,
            bindable: false,
            options: exports.MERGE_STRATEGY_OPTIONS,
            defaultValue: 'wait_all',
            widget: 'enum-select',
            section: CONSTANTS_SECTION,
            description: 'When to release the merge: wait for every branch, wait for any, or take the first arriving.',
            descriptionsI18n: {
                en: 'When to release the merge: wait for every branch, wait for any, or take the first arriving.',
                de: 'Wann die Zusammenführung weiterläuft: auf alle Branches warten, auf einen beliebigen warten oder den ersten eintreffenden nehmen.',
            },
        }, strategy),
        withValue({
            id: slotId(mergeSlug, 'collectMode'),
            name: 'collectMode',
            labelsI18n: { en: 'Collect Mode', de: 'Sammelmodus' },
            type: 'enum',
            required: true,
            bindable: false,
            options: exports.MERGE_COLLECT_OPTIONS,
            defaultValue: 'object',
            widget: 'enum-select',
            section: CONSTANTS_SECTION,
            description: 'Output shape: object keyed by port id, or ordered array.',
            descriptionsI18n: {
                en: 'Output shape: object keyed by port id, or ordered array.',
                de: 'Ausgabeform: Objekt nach Port-ID oder geordnetes Array.',
            },
        }, collectMode),
        withValue({
            id: slotId(mergeSlug, 'pairedBranchId'),
            name: 'pairedBranchId',
            labelsI18n: { en: 'Paired Branch', de: 'Zugehörige Verzweigung' },
            type: 'text',
            required: false,
            bindable: false,
            description: 'Step id of the Branch node that owns this Merge. Written by the UI at insert time.',
            descriptionsI18n: {
                en: 'Step id of the Branch node that owns this Merge. Written by the UI at insert time.',
                de: 'Step-ID der Branch-Node, zu der diese Zusammenführung gehört. Wird beim Einfügen durch die UI gesetzt.',
            },
            widget: 'hidden',
            section: CONSTANTS_SECTION,
        }, config?.pairedBranchId),
    ];
}
function buildMergeOutputSlots() {
    const mergeSlug = 'control-merge';
    return [
        {
            id: slotId(mergeSlug, 'merged'),
            name: 'merged',
            labelsI18n: { en: 'Merged', de: 'Zusammengeführt' },
            type: 'json',
            required: false,
            description: 'Collected payload from every upstream branch, shaped per collectMode.',
            descriptionsI18n: {
                en: 'Collected payload from every upstream branch, shaped per collectMode.',
                de: 'Gesammelter Payload aus allen Upstream-Branches, geformt nach Sammelmodus.',
            },
            section: OUTPUT_SECTION,
        },
    ];
}
function buildSplitOutputSlots() {
    const splitSlug = 'control-split';
    return [
        {
            id: slotId(splitSlug, 'item'),
            name: 'item',
            labelsI18n: { en: 'Item' },
            type: 'json',
            required: false,
            description: 'Current element emitted per iteration.',
            section: OUTPUT_SECTION,
        },
    ];
}
function buildFilterOutputSlots() {
    const filterSlug = 'control-filter';
    return [
        {
            id: slotId(filterSlug, 'filtered'),
            name: 'filtered',
            labelsI18n: { en: 'Filtered' },
            type: 'json',
            required: false,
            description: 'Sub-array of items matching the predicate.',
            section: OUTPUT_SECTION,
        },
    ];
}
function buildJoinOutputSlots() {
    const joinSlug = 'control-join';
    return [
        {
            id: slotId(joinSlug, 'joined'),
            name: 'joined',
            labelsI18n: { en: 'Joined' },
            type: 'json',
            required: false,
            description: 'Collected array.',
            section: OUTPUT_SECTION,
        },
    ];
}
function deriveControlNodeOutputSlots(engineType) {
    const type = engineType.startsWith('swr.')
        ? engineType.slice('swr.'.length)
        : engineType;
    switch (type) {
        case 'branch_xor':
        case 'branch_or':
        case 'branch_and':
            return buildBranchOutputSlots();
        case 'loop':
            return buildLoopOutputSlots();
        case 'merge':
            return buildMergeOutputSlots();
        case 'split':
            return buildSplitOutputSlots();
        case 'filter':
            return buildFilterOutputSlots();
        case 'join':
            return buildJoinOutputSlots();
        default:
            return null;
    }
}
